/**
 * 双端埋点（阿里云 QT + 火山引擎）
 *
 * - 与 head 脚本约定一致：QT `_hold: BLOCK` 后需 setUser / setUserProfile，再 start
 * - 火山：init 在 head 完成；start 由本模块在业务就绪后调用
 * - start() 调用前业务埋点入队；start 后 flush 并直发 SDK（公共字段写入事件 payload，非通参）
 *
 * @see https://help.aliyun.com/zh/document_detail/602417.html
 * @see https://www.volcengine.com/docs/6287/1278199?lang=zh
 */

/** PV 上报时 page_name，与数据平台约定（对齐双端预置语义） */
export const TRACKER_PAGE_SHOW_NAME = 'page_show' as const;

export interface ITrackerUser {
    userId: string;
}

export interface IDualTracker {
    setUser: (user: ITrackerUser) => void;
    setUserProfile: <T>(profile: T) => void;
    start: () => void;
    trackPV: <T>(pageKey: string, options?: T) => void;
    trackClick: <T>(eventCode: string, data?: T) => void;
    trackExposure: <T>(eventCode: string, data?: T) => void;
    trackCustom: <T>(eventCode: string, data?: T) => void;
}

type TPendingTrackKind = 'pv' | 'click' | 'exposure' | 'custom';

interface IPendingTrackEvent {
    kind: TPendingTrackKind;
    pageKey?: string;
    eventCode?: string;
    data?: Record<string, unknown>;
}

const isClient = (): boolean => typeof window !== 'undefined';

interface IQtCommand {
    action: string;
    arguments?: unknown[];
}

const pushQt = (command: IQtCommand): void => {
    const q = isClient() ? window.aplus_queue : undefined;
    if (!q) {
        // eslint-disable-next-line no-console
        console.warn('[tracker][qt] sdk not ready, skip:', command.action);
        return;
    }
    q.push(command);
};

// const pushVolc = (...args: unknown[]): void => {
//     const send = isClient() ? window.collectEvent : undefined;
//     if (!send) {
//         // eslint-disable-next-line no-console
//         console.warn('[tracker][volc] sdk not ready, skip:', args[0]);
//         return;
//     }
//     send(...(args as [string, ...unknown[]]));
// };

function logTrackCustom(eventCode: string, data?: Record<string, unknown>): void {
    // eslint-disable-next-line no-console
    console.log(
        '%c trackCustom %c %s',
        'background:#7c3aed;color:#fff;padding:2px 8px;border-radius:4px;font-weight:600',
        'color:#22d3ee;font-weight:600',
        eventCode,
        data
    );
}

export const createDualTracker = (): IDualTracker => {
    let hasStarted = false;
    let baseProfile: Record<string, unknown> = {};
    const pendingQueue: IPendingTrackEvent[] = [];

    const buildEventPayload = (data: Record<string, unknown>): Record<string, unknown> => ({
        ...data,
        ...baseProfile,
    });

    const sendTrackPV = (pageKey: string, options: Record<string, unknown> | undefined): void => {
        const payload = {
            ...buildEventPayload((options ?? {}) as Record<string, unknown>),
            page_name: TRACKER_PAGE_SHOW_NAME,
            page_key: pageKey,
        };

        pushQt({ action: 'aplus.sendPV', arguments: [{ is_auto: false }, payload] });
        // pushVolc('predefine_pageview', payload);
    };

    const sendTrackClick = (eventCode: string, data: Record<string, unknown> | undefined): void => {
        const payload = buildEventPayload((data ?? {}) as Record<string, unknown>);
        pushQt({ action: 'aplus.record', arguments: [eventCode, 'CLK', payload] });
        // pushVolc(eventCode, payload);
    };

    const sendTrackExposure = (eventCode: string, data: Record<string, unknown> | undefined): void => {
        const payload = buildEventPayload((data ?? {}) as Record<string, unknown>);
        pushQt({ action: 'aplus.record', arguments: [eventCode, 'EXP', payload] });
        // pushVolc(eventCode, payload);
    };

    const sendTrackCustom = (eventCode: string, data: Record<string, unknown> | undefined): void => {
        const payload = buildEventPayload((data ?? {}) as Record<string, unknown>);
        logTrackCustom(eventCode, payload);
        pushQt({ action: 'aplus.record', arguments: [eventCode, 'OTHER', payload] });
        // pushVolc(eventCode, payload);
    };

    const enqueuePending = (event: IPendingTrackEvent): void => {
        pendingQueue.push(event);
    };

    const flushPendingQueue = (): void => {
        while (pendingQueue.length > 0) {
            const event = pendingQueue.shift();
            if (!event) {
                break;
            }
            switch (event.kind) {
                case 'pv':
                    if (event.pageKey) {
                        sendTrackPV(event.pageKey, event.data);
                    }
                    break;
                case 'click':
                    if (event.eventCode) {
                        sendTrackClick(event.eventCode, event.data);
                    }
                    break;
                case 'exposure':
                    if (event.eventCode) {
                        sendTrackExposure(event.eventCode, event.data);
                    }
                    break;
                case 'custom':
                    if (event.eventCode) {
                        sendTrackCustom(event.eventCode, event.data);
                    }
                    break;
                default:
                    break;
            }
        }
    };

    const setUser = (user: ITrackerUser): void => {
        if (!user.userId) {
            // eslint-disable-next-line no-console
            console.warn('[tracker] setUser failed: userId is empty');
            return;
        }
        pushQt({ action: 'aplus.setMetaInfo', arguments: ['_user_id', user.userId] });
        // pushVolc('config', { user_unique_id: user.userId });
    };

    const setUserProfile = <T>(profile: T): void => {
        if (!profile || typeof profile !== 'object' || Object.keys(profile as object).length === 0) {
            // eslint-disable-next-line no-console
            console.warn('[tracker] setUserProfile skipped: empty profile');
            return;
        }
        baseProfile = { ...baseProfile, ...(profile as Record<string, unknown>) };
        pushQt({ action: 'aplus.record', arguments: ['$$_user_profile', 'OTHER', baseProfile] });
    };

    const start = (): void => {
        if (hasStarted) {
            return;
        }
        pushQt({ action: 'aplus.setMetaInfo', arguments: ['_hold', 'START'] });
        // pushVolc('start');
        hasStarted = true;
        flushPendingQueue();
    };

    const trackPV = <T>(pageKey: string, options?: T): void => {
        if (!hasStarted) {
            enqueuePending({ kind: 'pv', pageKey, data: (options ?? {}) as Record<string, unknown> });
            return;
        }
        sendTrackPV(pageKey, (options ?? {}) as Record<string, unknown>);
    };

    const trackClick = <T>(eventCode: string, data?: T): void => {
        if (!eventCode) {
            // eslint-disable-next-line no-console
            console.warn('[tracker] trackClick failed: eventCode is empty');
            return;
        }
        if (!hasStarted) {
            enqueuePending({ kind: 'click', eventCode, data: (data ?? {}) as Record<string, unknown> });
            return;
        }
        sendTrackClick(eventCode, (data ?? {}) as Record<string, unknown>);
    };

    const trackExposure = <T>(eventCode: string, data?: T): void => {
        if (!eventCode) {
            // eslint-disable-next-line no-console
            console.warn('[tracker] trackExposure failed: eventCode is empty');
            return;
        }
        if (!hasStarted) {
            enqueuePending({ kind: 'exposure', eventCode, data: (data ?? {}) as Record<string, unknown> });
            return;
        }
        sendTrackExposure(eventCode, (data ?? {}) as Record<string, unknown>);
    };

    const trackCustom = <T>(eventCode: string, data?: T): void => {
        if (!eventCode) {
            // eslint-disable-next-line no-console
            console.warn('[tracker] trackCustom failed: eventCode is empty');
            return;
        }
        if (!hasStarted) {
            enqueuePending({ kind: 'custom', eventCode, data: (data ?? {}) as Record<string, unknown> });
            return;
        }
        sendTrackCustom(eventCode, (data ?? {}) as Record<string, unknown>);
    };

    return { setUser, setUserProfile, start, trackPV, trackClick, trackExposure, trackCustom };
};

/** 默认单例：import { tracker } from '@/utils/tracker' */
export const tracker = createDualTracker();
